'use client';

import { useEffect, Suspense, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import posthog from 'posthog-js';
import { createClient } from '@/lib/supabase/client';
import { getClientUser } from '@/lib/supabase/client-user';
import { ensurePostHogBrowser } from '@/lib/posthog-browser';

const EXCLUDED_POSTHOG_EMAILS = new Set([
  'anujuchil@gmail.com',
]);

function normalizeEmail(email?: string | null) {
  return email?.trim().toLowerCase() ?? null;
}

function isExcludedPostHogUser(email?: string | null) {
  const normalizedEmail = normalizeEmail(email);
  return normalizedEmail ? EXCLUDED_POSTHOG_EMAILS.has(normalizedEmail) : false;
}

function PostHogPageView({ enabled }: { enabled: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (enabled && pathname && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      let url = window.origin + pathname;
      if (searchParams && searchParams.toString()) {
        url = url + `?${searchParams.toString()}`;
      }
      posthog.capture('$pageview', {
        $current_url: url,
      });
    }
  }, [enabled, pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      return;
    }

    const supabase = createClient();
    let isMounted = true;

    // Initialise before the auth round-trip resolves. Waiting for it would leave exception
    // autocapture off — and every error boundary reporting into a void — for the whole of
    // page bootstrap, which is exactly when the errors worth catching happen. The cost is
    // that an excluded user's first few hundred milliseconds are captured anonymously
    // before the opt-out below lands.
    ensurePostHogBrowser();

    const syncPostHogForUser = (user: SupabaseUser | null) => {
      if (isExcludedPostHogUser(user?.email)) {
        posthog.reset();
        posthog.opt_out_capturing();
        if (isMounted) {
          setIsEnabled(false);
        }
        return;
      }

      posthog.opt_in_capturing();

      if (user) {
        // The Supabase user id stays the distinct id — every server-side capture keys
        // off it, so changing it would fork the person profile. The email rides along
        // as a person property so a profile in the PostHog UI is identifiable without
        // a lookup back into Supabase. It is sent on every identify rather than once,
        // because a profile that lost the property (address change, manual wipe) has
        // no other path back to it.
        const email = normalizeEmail(user.email);
        posthog.identify(user.id, email ? { email } : undefined);
      } else {
        posthog.reset();
      }

      if (isMounted) {
        setIsEnabled(true);
      }
    };

    getClientUser().then(({ data: { user } }) => {
      if (isMounted) {
        syncPostHogForUser(user);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      syncPostHogForUser(session?.user ?? null);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <PostHogPageView enabled={isEnabled} />
      </Suspense>
      {children}
    </>
  );
}

